import { useCallback, useEffect, useMemo } from "react";
import { syncStoreToLocalStorage } from "src/js/stores/site/effects/syncStoreToLocalStorage";
import {
  clearSiteSiteReportConfiguration,
  setSiteSiteReportConfiguration,
} from "src/js/stores/site/actions/siteReportConfiguration";
import { SiteReportConfiguration, siteStore } from "src/js/stores/site/store";
import { useSnapshot } from "valtio";
import { calculateNodeState, getChildPaths, getDescendantPaths } from "./pathUtils";

const EMPTY_CONFIGURATION: SiteReportConfiguration = {};

export function useSiteReportConfiguration(siteId: string) {
  const { siteReportConfiguration } = useSnapshot(siteStore);

  useEffect(() => {
    syncStoreToLocalStorage();
  }, []);

  const setConfiguration = useCallback(
    (siteReportConfiguration: SiteReportConfiguration) =>
      setSiteSiteReportConfiguration(siteId, siteReportConfiguration),
    [siteId]
  );

  const clearConfiguration = useCallback(() => clearSiteSiteReportConfiguration(siteId), [siteId]);

  return [siteReportConfiguration[siteId] ?? EMPTY_CONFIGURATION, { setConfiguration, clearConfiguration }] as const;
}

export const EMPTY_IDS: string[] = [];

export function useSiteReportConfigurationState(key: keyof SiteReportConfiguration, siteId: string, ids: string[]) {
  const [reportConfiguration, { setConfiguration }] = useSiteReportConfiguration(siteId);

  const rawValue = reportConfiguration[key];

  const hiddenIds = useMemo<string[]>(() => {
    if (key === "checkboxPaths") {
      const checkboxPaths = rawValue as Record<string, boolean> | undefined;
      if (!checkboxPaths) return EMPTY_IDS;

      return Object.keys(checkboxPaths).filter((path) => checkboxPaths[path] === false);
    }

    // For legacy array-based keys
    return (rawValue as string[] | undefined) ?? EMPTY_IDS;
  }, [rawValue, key]);

  const setVisible = useCallback(
    (excludedIds: string[]) => {
      if (key === "checkboxPaths") {
        const currentPaths = reportConfiguration.checkboxPaths || {};
        const updatedPaths = { ...currentPaths };

        ids.forEach((id) => {
          updatedPaths[id] = true;
        });

        excludedIds.forEach((excludedId) => {
          updatedPaths[excludedId] = false;
        });

        setConfiguration({ ...reportConfiguration, checkboxPaths: updatedPaths });
      } else {
        // For legacy array-based keys
        setConfiguration({ ...reportConfiguration, [key]: excludedIds });
      }
    },
    [key, reportConfiguration, setConfiguration, ids]
  );

  // Checkbox path-based methods
  const getCheckboxState = useCallback(
    (path: string): boolean => {
      const checkboxPaths = reportConfiguration.checkboxPaths || {};
      return checkboxPaths[path] !== false; // Default to true if not set
    },
    [reportConfiguration.checkboxPaths]
  );

  const setCheckboxState = useCallback(
    (path: string, checked: boolean) => {
      const currentPaths = reportConfiguration.checkboxPaths || {};
      const updatedPaths = { ...currentPaths, [path]: checked };
      setConfiguration({ ...reportConfiguration, checkboxPaths: updatedPaths });
    },
    [reportConfiguration, setConfiguration]
  );

  const setBulkCheckboxState = useCallback(
    (updates: Record<string, boolean>) => {
      const currentPaths = reportConfiguration.checkboxPaths || {};
      const updatedPaths = { ...currentPaths, ...updates };
      setConfiguration({ ...reportConfiguration, checkboxPaths: updatedPaths });
    },
    [reportConfiguration, setConfiguration]
  );

  const toggleCheckbox = useCallback(
    (path: string) => {
      const currentState = getCheckboxState(path);
      setCheckboxState(path, !currentState);
    },
    [getCheckboxState, setCheckboxState]
  );

  const [allVisible, allHidden] = useMemo(
    () => [!hiddenIds.length, ids.every((id) => hiddenIds.includes(id))] as const,
    [hiddenIds, ids]
  );

  const toggleAll = useCallback(() => {
    setVisible(allVisible ? ids : EMPTY_IDS);
  }, [allVisible, ids, setVisible]);

  const getNodeState = useCallback(
    (nodePath: string, allPaths: string[]): boolean | "indeterminate" => {
      const checkboxPaths = reportConfiguration.checkboxPaths || {};
      const hiddenPaths = Object.keys(checkboxPaths).filter((path) => checkboxPaths[path] === false);
      return calculateNodeState(nodePath, hiddenPaths, allPaths);
    },
    [reportConfiguration.checkboxPaths]
  );

  const toggleId = useCallback(
    (id: string) => {
      if (key === "checkboxPaths") {
        const currentState = getCheckboxState(id);
        setCheckboxState(id, !currentState);
      } else {
        // For legacy array-based keys
        setVisible(hiddenIds.includes(id) ? hiddenIds.filter((hid: string) => hid !== id) : [...hiddenIds, id]);
      }
    },
    [key, hiddenIds, setVisible, getCheckboxState, setCheckboxState]
  );

  const selectAllChildren = useCallback(
    (parentPath: string, allPaths: string[]) => {
      const childPaths = getDescendantPaths(parentPath, allPaths);
      const currentPaths = reportConfiguration.checkboxPaths || {};
      const updatedPaths = { ...currentPaths };

      updatedPaths[parentPath] = true;
      childPaths.forEach((path) => {
        updatedPaths[path] = true;
      });

      setConfiguration({ ...reportConfiguration, checkboxPaths: updatedPaths });
    },
    [reportConfiguration, setConfiguration]
  );

  const updateParentState = useCallback(
    (childPath: string, allPaths: string[]) => {
      const checkboxPaths = reportConfiguration.checkboxPaths || {};
      const updatedPaths = { ...checkboxPaths };

      let currentPath = childPath;
      while (currentPath.includes(".")) {
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf("."));
        const childPaths = getChildPaths(parentPath, allPaths);

        if (childPaths.length > 0) {
          const allChildrenChecked = childPaths.every((path) => updatedPaths[path] !== false);
          const anyChildChecked = childPaths.some((path) => updatedPaths[path] !== false);

          if (allChildrenChecked) {
            updatedPaths[parentPath] = true;
          } else if (!anyChildChecked) {
            updatedPaths[parentPath] = false;
          }
          // For indeterminate state, we don't set a specific value
        }

        currentPath = parentPath;
      }

      setConfiguration({ ...reportConfiguration, checkboxPaths: updatedPaths });
    },
    [reportConfiguration, setConfiguration]
  );

  return {
    hiddenIds,
    allVisible,
    allHidden,
    toggleId,
    toggleAll,
    // New checkbox path-based API
    getCheckboxState,
    setCheckboxState,
    setBulkCheckboxState,
    toggleCheckbox,
    getNodeState,
    selectAllChildren,
    updateParentState,
  };
}
